import $ from 'jquery';
import {getMetaContent, getSelectedFilter} from "./Util";

class DataLayerTracking {

	static debugMode = false;

	constructor() {
		this.currency = getMetaContent( '_isoCurrency' ) || 'EUR';

		this.initBasketChange();
		this.initProductImpressionsEvent();
		this.initNotificationTrackEvent();
		this.initShippingMethodNextClick();
		this.initPaymentMethodNextClick();
		this.initProductDetailPageEvent();
		this.initProductClickEvent();
		this.initProductListPageFilterSubmit();
	}

	initProductListPageFilterSubmit = () =>  {
		window.addEventListener( 'submit_chip.acl', ( ev ) => {
			this.filterEvent( ev.detail.fieldset, ev.detail.chip.identifier );
		} );
	}

	filterEvent = ( fieldset, identifier ) =>  {
		// Skip for sortorder and tree filters
		if ( identifier === 'sortorder' || identifier === 'tree' ) return;

		const selectedFilters = getSelectedFilter( fieldset, identifier );

		Object.entries( selectedFilters ).forEach( ( [ key, values ] ) => {
			this.sendDatalayerEvent( 'gtmPkFilter', {
				gtmPkFilterType: key,
				gtmPkFilterValues: values
			} );
		} );
	}

	initBasketChange = () => {
		window.addEventListener( 'minibasket_changed.acl', ( ev ) => {
			const { basketChanged }  = ev.detail;

			// Skip on thankyou page
			if ( window.v_cl === 'thankyou' ) {
				return;
			}

			// Fire addToCart or removeFromCart events
			basketChanged.forEach( ( { action, item, diffamount, quickview } ) => {
				if ( action === 'add' ) {
					this.addToCartEvent( item, diffamount, quickview );
				} else if ( action === 'remove' ) {
					this.removeFromCartEvent( item, diffamount );
				}
			} );
		} );
	}

	addToCartEvent = ( basketItem, quantity, quickview = false ) => {
		const properties = {
			ecommerce: {
				currencyCode: this.currency,
				add: {
					products: [
						{
							...this.getBasketProductData( basketItem ),
							quantity
						}
					]
				}
			},
			shoplayer: quickview ? 'quickview_popup' : 'pdp'
		};

		this.sendDatalayerEvent( 'addToCart', properties );
	}

	removeFromCartEvent = ( basketItem, quantity ) => {
		const properties = {
			ecommerce: {
				remove: {
					products: [
						{
							...this.getBasketProductData( basketItem ),
							quantity
						}
					]
				}
			}
		};

		this.sendDatalayerEvent( 'removeFromCart', properties );
	}

	getBasketProductData( basketItem ) {
		const { desc: name, title: brand, oxid: dimension10, ean: dimension11, amount: quantity, ...rest } = basketItem;
		return { ...rest, name, brand, dimension10, dimension11, quantity };
	}


	/**
	 * Track if product is visible in viewport
	 */
	initProductImpressionsEvent = () => {
		const trackedProducts = new Set();
		const maxBatchSize = 32;
		let batchTimeout = null;
		let batchedProducts = [];
		const defaultProductSelector = '.en_mdl_product';
		const carouselProductSelector = '.mdl_productcarousel_item';
		const selectorsToWatchFor = defaultProductSelector + ', ' + carouselProductSelector;

		const sendBatch = () => {
			if ( !batchedProducts.length ) return;

			this.sendDatalayerEvent( 'productImpression', {
				'ecommerce': {
					'currencyCode': this.currency,
					'impressions': [...batchedProducts]
				}
			} );

			batchedProducts = [];
			batchTimeout = null;
		};

		const scheduleBatch = () => {
			if ( batchTimeout !== null ) return;
			batchTimeout = setTimeout( sendBatch, 1000 ); // Fetch all products in timeframe of 1 second and then send batch
		};

		const observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( ( { target: el, isIntersecting } ) => {
				if ( !isIntersecting ) return;

				const id = el.dataset.eecId;
				if ( trackedProducts.has( id ) ) return;

				let type, position;
				if ( el.matches( defaultProductSelector ) ) {
					type = 'default';
					position = Array.from( el.parentNode.children )
						.filter( child => child.matches( defaultProductSelector ) )
						.indexOf( el );
				} else if ( el.matches( carouselProductSelector ) ) {
					type = 'carousel';
					position = Array.from( el.parentNode.children ).indexOf( el );
				}

				trackedProducts.add( id );
				batchedProducts.push( this.getProductData( id, el, position, type ) );

				scheduleBatch();
				if ( batchedProducts.length >= maxBatchSize ) sendBatch();
			} );
		}, { threshold: 0.5 } );

		const observeNewProducts = () => {
			document.querySelectorAll( selectorsToWatchFor ).forEach( product => {
				if ( !trackedProducts.has( product.dataset.eecId ) ) observer.observe( product );
			} );
		};

		// Oberserve initial products
		observeNewProducts();

		// Observe dynamically added products
		const mutationObserver = new MutationObserver( ( mutations ) => {
			mutations.forEach( mutation => {
				mutation.addedNodes.forEach( node => {
					// Check for product directly or in nested dom
					if ( node.nodeType === 1 && ( node.matches( selectorsToWatchFor ) || node.querySelector( selectorsToWatchFor ) ) ) {
						observeNewProducts();
					}
				} );
			} );
		} );

		mutationObserver.observe( document.body, { childList: true, subtree: true } );
	}

	getProductData( id, productEl, position, type = 'default' ) {
		const { eecName: name, eecPrice: price, eecBrand: brand, eecCategory: category, eecList: list } = productEl.dataset;

		return {
			id,
			name,
			price,
			brand,
			category,
			position,
			...( type === 'default' && { list } )
		};
	}

	/**
	 * Track if notification is visible in checkout
	 */
	initNotificationTrackEvent = () => {
		const tracks = document.querySelectorAll( '.notification--track' );

		tracks.forEach( track => {
			const properties = {
				gtmMsgLayerText: track.textContent,
				gtmMsgLayerPlacement: track.dataset.position, // or "cart" or sth. else
				gtmMsgLayerCode: track.dataset.errorcode, // if there are different types of errors and we want to rely on the number instead of (maybe translated) text
				gtmMsgLayerType: 'error' // maybe there can also be warnings, info messages, etc.?
			};

			this.sendDatalayerEvent( 'gtmMsgLayer', properties );
		} );
	}

	/**
	 * Track user click on next button in checkout step shipping
	 */
	initShippingMethodNextClick() {
		document.addEventListener( 'submit', ( event ) => {
			const target = event.target;
			// Prüfen, ob der Container die Shipping-Step ist
			if ( target.matches( '.form-user-shipping' ) && target.closest( '.mdl_checkout_process' ) ) {
				const selectedOption = target.querySelector( 'input[name="invadr[oxuser__marketpreferedshippingflag]"]:checked' );
				if ( !selectedOption ) return;

				const optionTitle = selectedOption.dataset.eecTitle;
				const properties = {
					ecommerce: {
						checkout_option: {
							actionField: {
								step: 2,
								option: optionTitle
							}
						}
					}
				};

				this.sendDatalayerEvent( 'checkoutOption', properties );
			}
		} );
	}

	/**
	 * Track user click on next button in checkout step payment
	 */
	initPaymentMethodNextClick = () => {
		document.addEventListener( 'submit', ( ev ) => {
			const target = ev.target;

			if ( target.matches( '.form-user-payment' ) && target.closest( '.mdl_checkout_process' ) ) {
				const selectedOption = target.querySelector( 'input[name="paymentid"]:checked' );
				if ( !selectedOption ) return;

				const optionTitle = selectedOption.dataset.eecTitle;
				const properties = {
					ecommerce: {
						checkout_option: {
							actionField: {
								step: 3,
								option: optionTitle
							}
						}
					}
				};

				this.sendDatalayerEvent( 'checkoutOption', properties );
			}
		} );
	}

	initProductDetailPageEvent() {
		// PDP opened
		if ( window.v_cl === 'details' ) {
			document.addEventListener( 'finished_change_variant', () => {
				this.productDetailPageEvent( false )
			} );
		}

		// Quickview for product opened
		const quickviewContainer = document.querySelector( '#popup_article' );
		if ( quickviewContainer ) {
			quickviewContainer.addEventListener( 'load_article_detail_popup.acl', () => {
				this.productDetailPageEvent( true );
			} );
		}
	}

	initProductClickEvent = () => {
		document.addEventListener( 'click', ( event ) => {
			const target = event.target;

			const selectors = [
				'.en_mdl_product',
				'.mdl_productcarousel_item',
				'.mdl_search__suggestions-product',
			];

			const productEl = target.closest( selectors.join( ', ' ) );

			if( !productEl ) return;
			const quickviewButton = target.closest( '.en_mdl_product__cart-button' );

			const isQuickview = Boolean( quickviewButton );

			this.productClickEvent( productEl, isQuickview );
		} );
	}

	productDetailPageEvent = ( quickview = false ) => {
		const selector = `${quickview ? '#popup_article ' : ''}.article_submit_wrapper .en_mdl_article_detail__button`;
		const holder = document.querySelector( selector );
		if ( !holder ) return;

		const {
			eecId: id,
			eecName: name,
			eecPrice: price,
			eecBrand: brand,
			eecCategory: category,
			eecOxid: dimension10
		} = holder.dataset;

		const shopDomain = document.querySelector( 'meta[name="_ShopDomain"]' )?.content || '';
		let list = '(external)';

		if ( quickview ) {
			list = window.location.pathname;
		} else if ( document.referrer.startsWith( shopDomain ) ) {
			list = document.referrer.split( '?' )[0].replace( window.location.origin, '' );
		}

		const properties = {
			gtmProductDataRefresh: 'updated',
			ecommerce: {
				currencyCode: this.currency,
				detail: {
					actionField: { list },
					products: [{ id, name, price, brand, category, dimension10 }]
				}
			},
			shoplayer: quickview ? 'quickview_popup' : 'pdp'
		};

		this.sendDatalayerEvent( 'productDetailPage', properties );
	}

	productClickEvent( elem, quickview = false ) {
		const {
			eecId: id,
			eecName: name,
			eecPrice: price,
			eecBrand: brand,
			eecCategory: category,
			eecPosition: position
		} = elem.dataset;

		const propertis = {
			'ecommerce': {
				'currencyCode': this.currency,
				'click': {
					actionField: { list: window.location.pathname },
					products: [{ id, name, price, brand, category, position }]
				}
			},
			...( quickview && { shopLayer: 'quickview_popup' } )
		}

		this.sendDatalayerEvent( 'productClick', propertis );
	}

	sendDatalayerEvent( name, properties = {} ) {
		// Check if name is set
		if ( !name ) {
			return;
		}

		// Check if DY api is available
		if ( typeof window.dataLayer === "undefined" || !Array.isArray( window.dataLayer ) ) {
			return;
		}

		if( DataLayerTracking.debugMode ) {
			console.log( JSON.stringify( {
				name: name,
				...properties
			} ) );
		}

		window.dataLayer.push( {
			event: name,
			...properties
		} );
	}
}

const initDataLayerTracking = function( ) {
	new DataLayerTracking();
}

export {
	DataLayerTracking as default,
	initDataLayerTracking
}
